export const ThesisActionNotificationDialogKeys = {
  ADD_EXAMINER: 'addExaminerDialog',
  PA_ADD_CHAIR: 'paAddChairDialog',
  CANDIDATE_UPDATE_EXAMINER_COI: 'candidateUpdateExaminerCOIDialog',
  GS_APPROVAL: 'gsApprovalDialog',
  PA_COI_FORM: 'paCOIFormDialog',
  EXAMINER_COI_FORM: 'examinerCOIFormDialog',
} as const

export const ThesisActionNotificationActionIds = {
  ASSIGN_CHAIR: 'assign-chair',
  NOMINATE_EXAMINERS: 'nominate-examiners',
  NOMINATE_ONE_EXAMINER: 'nominate-one-examiner',
  PROVIDE_ADDITIONAL_EXAMINER_DETAILS: 'provide-additional-examiner-details',
  EXAMINER_PROVIDE_COI_DISCLOSURE: 'examiner-provide-coi-disclosure',
  EXAMINER_UPDATE_GRAD_SCHOOL_REQUESTED_DETAILS: 'examiner-update-grad-school-requested-details',
  CANDIDATE_REVIEW_EXAMINER: 'candidate-review-examiner',
  GRAD_SCHOOL_REVIEW_EXAMINER: 'grad-school-review-examiner',
}

export type ThesisExaminerActionNotificationDataType = {
  actionDescription: string
  actionId: string
  notificationDisplayStatuses: string[]
  userRole: string
  message: string
  buttonText: string
  relatedExaminerId: number | null
  dialog: string
}

/**
 * Thesis examiner nomination action notification data
 *
 * @TODO future improvement: This could be refactored down to a single reduce/filter function where it checks for the user role and the examiner status. Potentially a few variables can be eliminated.
 */
export const actionNotificationData = (candidateName?: string): ThesisExaminerActionNotificationDataType[] => [
  {
    actionDescription: 'Assign chair',
    actionId: ThesisActionNotificationActionIds.ASSIGN_CHAIR,
    notificationDisplayStatuses: [],
    userRole: 'user-thesis-principal-advisor',
    message: `You are required to assign the Chair of Examiners for the HDR candidature of ${candidateName}.`,
    buttonText: 'Assign',
    relatedExaminerId: null,
    dialog: ThesisActionNotificationDialogKeys.PA_ADD_CHAIR,
  },
  {
    actionDescription: 'Nominate examiners',
    actionId: ThesisActionNotificationActionIds.NOMINATE_EXAMINERS,
    notificationDisplayStatuses: [],
    userRole: 'user-thesis-principal-advisor',
    message: `You must nominate examiners for the HDR thesis of ${candidateName}.`,
    buttonText: 'Nominate',
    relatedExaminerId: null,
    dialog: ThesisActionNotificationDialogKeys.ADD_EXAMINER,
  },
  {
    actionDescription: 'Nominate one examiner',
    actionId: ThesisActionNotificationActionIds.NOMINATE_ONE_EXAMINER,
    notificationDisplayStatuses: [],
    userRole: 'user-thesis-principal-advisor',
    message: `You must nominate an additional examiner for the HDR thesis of ${candidateName}.`,
    buttonText: 'Nominate',
    relatedExaminerId: null,
    dialog: ThesisActionNotificationDialogKeys.ADD_EXAMINER,
  },
  {
    actionDescription: 'Provide additional examiner details',
    actionId: ThesisActionNotificationActionIds.PROVIDE_ADDITIONAL_EXAMINER_DETAILS,
    notificationDisplayStatuses: ['INITIAL_CHANGE_REQUEST_BY_GRADUATE_SCHOOL'],
    userRole: 'user-thesis-principal-advisor',
    message:
      'The Graduate School has requested additional details for your nominated thesis examiner: ${examinerName}.',
    buttonText: 'Update',
    relatedExaminerId: null,
    dialog: ThesisActionNotificationDialogKeys.PA_COI_FORM,
  },
  {
    actionDescription: 'Examiner provide COI disclosure',
    actionId: ThesisActionNotificationActionIds.EXAMINER_PROVIDE_COI_DISCLOSURE,
    notificationDisplayStatuses: ['EXAMINER_COI_REVIEW'],
    userRole: 'user-thesis-examiner',
    message: `As a nominated examiner for ${candidateName} you must provide a conflict of interest disclosure. Please complete and submit the form.`,
    buttonText: 'Submit',
    relatedExaminerId: null,
    dialog: ThesisActionNotificationDialogKeys.EXAMINER_COI_FORM,
  },
  {
    actionDescription: 'Examiner update Graduate School requested details',
    actionId: ThesisActionNotificationActionIds.EXAMINER_UPDATE_GRAD_SCHOOL_REQUESTED_DETAILS,
    notificationDisplayStatuses: ['FINAL_CHANGE_REQUEST_BY_GRADUATE_SCHOOL'],
    userRole: 'user-thesis-examiner',
    message: `The Graduate School has requested that you update your details for consideration as an examiner.`,
    buttonText: 'Update',
    relatedExaminerId: null,
    dialog: ThesisActionNotificationDialogKeys.EXAMINER_COI_FORM,
  },
  {
    actionDescription: 'Candidate review examiner',
    actionId: ThesisActionNotificationActionIds.CANDIDATE_REVIEW_EXAMINER,
    notificationDisplayStatuses: ['CANDIDATE_COI_REVIEW'],
    userRole: 'user-thesis-submission',
    message:
      'Your Principal Advisor has nominated ${examinerName} to your examination committee. Please review and provide your conflict of interest disclosure.',
    buttonText: 'Review',
    relatedExaminerId: null,
    dialog: ThesisActionNotificationDialogKeys.CANDIDATE_UPDATE_EXAMINER_COI,
  },
  {
    actionDescription: 'Graduate School review examiner',
    actionId: ThesisActionNotificationActionIds.GRAD_SCHOOL_REVIEW_EXAMINER,
    notificationDisplayStatuses: ['GRADUATE_SCHOOL_INITIAL_REVIEW', 'FINAL_CHANGE_REQUEST_BY_GRADUATE_SCHOOL'],
    userRole: 'grad-school-user',
    message: 'Examiner nomination and conflict of interest disclosures have been submitted for your review.',
    buttonText: 'Review',
    relatedExaminerId: null,
    dialog: ThesisActionNotificationDialogKeys.GS_APPROVAL,
  },
]
